import { createSelector } from 'reselect';
import { State } from 'flow/types/State';
import {
    selectOwnUserHasCapabilityCCPro,
    selectOwnUserHasCapability,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';
import { Capabilities } from 'flow/utils/capability-utils';

export const selectIsAdmin = (state: State) =>
    state.app.getIn(['extension', 'admin'], false);

// Mirrors the backend's rule for a calendar's edit access: an admin can edit
// every calendar, everyone else only their own. Unlike the calendar's
// `editable` flag, this stays true for a calendar synced from an external
// source, which the backend always marks as not editable. Takes any entity key,
// since the calendar views are shared with PBX entities; those resolve to the
// admin check alone.
export const selectHasCalendarEditAccess = (state: State, key: string) =>
    selectIsAdmin(state) || key === selectOwnExtensionKey(state);

export const selectOwnChatUserKey = (state: State) =>
    state.app.getIn(['extension', 'chatUserKey']);

export const selectOwnUserHasOmnichannelFeature = (state: State) =>
    state.app.getIn(['extension', 'hasOmnichannelFeature'], false);

export const selectHasReturnBlindTransferFeature = (state: State) =>
    state.app.getIn(['extension', 'hasReturnBlindTransferFeature'], false);

const selectHasSupervisorFeature = (state: State) =>
    state.app.getIn(['extension', 'hasSupervisorFeature'], false);

export const selectCanAccessSupervisorView = (state: State) =>
    selectHasSupervisorFeature(state) || selectOwnUserHasCapabilityCCPro(state);
// Comment from Max on selectCanAccessSupervisorView: I've simply copy-pasted the logic as it was in the components,
// checking for either hasSupervisorFeature or CCPro in order to see My Overview.
// It's been like this for a while, although it was, I think, unintentional.
// See https://telavox.atlassian.net/browse/OMNI-1436 for more information.

export const selectHasCapabilityLegacyTranscribeOnCall = (state: State) =>
    selectOwnUserHasCapability(state, Capabilities.TRANSCRIBE_ON_CALL);

export const selectHasCapabilityPersonalTranscription = (state: State) =>
    selectOwnUserHasCapability(state, Capabilities.PERSONAL_TRANSCRIPTION);

export const selectHasCapabilityBusinessTranscription = (state: State) =>
    selectOwnUserHasCapability(state, Capabilities.BUSINESS_TRANSCRIPTION);

export const selectHasTranscriptionCapabilityBusinessOrPersonal =
    createSelector(
        selectHasCapabilityPersonalTranscription,
        selectHasCapabilityBusinessTranscription,
        (personal, business) => personal || business,
    );

export const selectHasSomeTranscriptionCapability = createSelector(
    selectHasCapabilityLegacyTranscribeOnCall,
    selectHasTranscriptionCapabilityBusinessOrPersonal,
    (legacy, businessOrPersonal) => legacy || businessOrPersonal,
);

export const selectCapabilitiesLoaded = (state: State) =>
    state.app.getIn(['extension', 'capabilities'])?.size > 0;
